import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';

// Mui
import { Box, Grid, Modal, Divider, Typography, IconButton, Button, Tooltip, List, ListItem, ListItemText, Card, CardMedia, TextField, Icon, Chip, CircularProgress } from '@mui/material';
import { Close, Event as EventIcon } from '@mui/icons-material';
import { red } from '@mui/material/colors';
import Swal from 'sweetalert2';

// Validations
import { GalleryPPM } from '@data/schemas/ppmSchema';
import { GalleryInterface, } from '@data/interfaces/ppmInterface';

//SERVICES
import ppmServices from '@services/PpmServices';
import moment from 'moment';

const FormGaleria = (props) => {
    const {
        setModalOpen,
        modalOpen,
        params = {},
        title = 'Actualización de galería de evento',
        setData,
    } = props;

    const [deletedImages, setDeletedImages] = useState([]);

    const formik = useFormik({
        initialValues: GalleryInterface,
        validationSchema: GalleryPPM,
        enableReinitialize: false,
        validateOnChange: true,
        onSubmit: (values, actions) => {
            setData({
                ...values,
                deletedImages
            });
        }
    });

    const resetParams = () => {
        setFile(null);
        setSelectedEvent([]);
        formik.resetForm();
    };

    const handleEditModalClose = () => {
        setModalOpen(false);
    };

    const [eventos, setEventos] = useState([]);
    const [filteredEventos, setFilteredEventos] = useState([]);
    const [loading, setLoading] = useState(false)

    // Obtener eventos PPM
    const getEvents = async (data) => {
        setLoading(true)
        try {
            const result = await ppmServices.getEventsGallery();
            const { results, response, message } = result;

            if (results) {
                setEventos(response.data);
                setFilteredEventos(response.data);
            } else {
                throw new Error(message);
            }
        } catch (e) {
            Swal.fire({
                title: e.message,
                icon: 'warning',
                customClass: {
                    container: 'modal-alert',
                },
            });
        }
        finally {
            setLoading(false)
        }
    };

    useEffect(() => {
        getEvents();
    }, []);

    const [searchTerm, setSearchTerm] = useState('');

    // Filtrar eventos
    useEffect(() => {
        const filtered = eventos.filter(perception =>
            perception.Actividad.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredEventos(filtered);
    }, [searchTerm, eventos]);

    const [selectedEvent, setSelectedEvent] = useState([]);

    const handleToggleEvent = (perception) => {
        setSelectedEvent([perception]);
        formik.setFieldValue('idEvento', perception.id);
    };

    const [file, setFile] = useState(null);

    const handleRemoveImage = (index) => {
        const updatedFiles = [...formik.values.files];
        const removedFile = updatedFiles[index];

        // Si la imagen tiene un id, agregarlo a deletedImages
        if (removedFile.id) {
            setDeletedImages((prev) => [...new Set([...prev, removedFile.id])]);
        }

        // Eliminar la imagen del array de files
        updatedFiles.splice(index, 1);

        // Actualizar los archivos en Formik
        formik.setFieldValue("files", updatedFiles);

        // Limpiar el valor de "newFiles" si es necesario
        if (formik.values.newFiles) {
            const updatedNewFiles = formik.values.newFiles.filter((file, i) => i !== index);
            formik.setFieldValue("newFiles", updatedNewFiles);
        }
    };

    const handleAddImage = () => {
        if (file) {
            const reader = new FileReader();

            reader.onloadend = () => {
                const base64String = reader.result;

                let fileName = file.name;
                fileName = fileName.replace(/\s+/g, '_');
                fileName = fileName.replace(/[^a-zA-Z0-9-_.]/g, '');

                formik.setFieldValue('files', [
                    ...formik.values.files,
                    {
                        type: 'img',
                        file: base64String,
                        fileNameImg: fileName
                    }
                ]);

                // Resetear el archivo y el campo seleccionado
                setFile(null);
            };

            reader.readAsDataURL(file);
        }
    };

    const handleFileChange = (e) => {
        const uploadedFile = e.target.files[0];
        e.target.value = null;

        if (uploadedFile) {
            // Guardamos el archivo seleccionado para la vista previa
            setFile(uploadedFile);
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setFilteredEventos(eventos);
    };

    const verify = (name) => {
        if (!formik.touched || !formik.touched[name]) return {};
        if (!formik.errors || !formik.errors[name]) return {};

        return {
            error: formik.touched[name] && Boolean(formik.errors[name]),
            helperText: formik.touched[name] && formik.errors[name],
        };
    };

    const [isDisabled, setIsDisabled] = useState(false);

    useEffect(() => {
        if (params.idEvento) {
            // Buscar el evento correspondiente en los eventos filtrados
            const eventToSelect = filteredEventos.find(event => event.id === params.idEvento);

            if (eventToSelect) {
                setIsDisabled(true);
                setSelectedEvent([eventToSelect]);
                formik.setFieldValue('idEvento', eventToSelect.id);
                formik.setFieldValue('files', params?.files);
            }
        }
    }, [params, filteredEventos]);

    return (
        <Modal
            open={modalOpen}
            disableEscapeKeyDown
            aria-labelledby="modal-edit-title"
            aria-describedby="modal-edit-description"
        >
            <Box>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        height: '90%',
                        maxWidth: '90%',
                        bgcolor: 'background.paper',
                        borderRadius: 4,
                        boxShadow: 24,
                        p: 4,
                        overflowY: 'auto',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography id="modal-edit-title" variant="h6" component="h2">
                            {title}
                        </Typography>
                        <Tooltip title="Cerrar">
                            <IconButton onClick={handleEditModalClose}>
                                <Close />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                            <Card sx={{ width: "100%", height: "100%", marginTop: 2, borderRadius: "9px", boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)" }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mx: 2 }}>
                                    <Box sx={{ my: 2, display: "flex", alignItems: 'center' }}>
                                        <EventIcon sx={{ ml: 2, mr: 1, color: "rgb(0,55,149)" }} />
                                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "rgb(0,55,149)" }}>
                                            Eventos ({filteredEventos.length})
                                        </Typography>
                                    </Box>
                                </Box>
                                <Divider />

                                <Box sx={{ my: 2, px: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Buscar evento"
                                        variant="outlined"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            endAdornment: (
                                                searchTerm && (
                                                    <IconButton onClick={handleClearSearch}>
                                                        <Close />
                                                    </IconButton>
                                                )
                                            )
                                        }}
                                        disabled={isDisabled}
                                    />
                                </Box>

                                <Divider />
                                <Box>
                                    <Box sx={{ maxHeight: 400, minHeight: 410, overflowY: "auto" }}>
                                        <List sx={{ padding: 0, margin: 0 }}>
                                            {loading ? (
                                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", mt: 4 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : (
                                                <List sx={{ padding: 0, margin: 0, width: "100%" }}>
                                                    {filteredEventos.length > 0 ? (
                                                        filteredEventos.map((perception, index) => {
                                                            const isSelected = selectedEvent.some((item) => item.id === perception.id);
                                                            return (
                                                                <div key={index}>
                                                                    <ListItem button onClick={() => handleToggleEvent(perception)} selected={isSelected} disabled={isDisabled}>
                                                                        <ListItemText
                                                                            primary={
                                                                                <Box display="flex" justifyContent="space-between" width="100%">
                                                                                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{perception.Actividad}</Typography>
                                                                                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "gray" }}>
                                                                                        {moment(perception.FechaInicio, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY")}
                                                                                    </Typography>
                                                                                </Box>
                                                                            }
                                                                            secondary={<Typography sx={{ fontSize: 14, fontWeight: 500, color: "gray" }}>{perception.address}</Typography>}
                                                                        />
                                                                    </ListItem>
                                                                    {index < filteredEventos.length - 1 && <Divider />}
                                                                </div>
                                                            );
                                                        })
                                                    ) : (
                                                        <ListItem>
                                                            <ListItemText
                                                                primary={
                                                                    <Typography sx={{ fontSize: 16, fontWeight: 600, textAlign: "center", color: "gray" }}>
                                                                        No hay eventos disponibles
                                                                    </Typography>
                                                                }
                                                            />
                                                        </ListItem>
                                                    )}
                                                </List>
                                            )}
                                        </List>
                                    </Box>
                                    <Divider />
                                    <Box sx={{ minHeight: 25, display: "flex", alignItems: "center" }}>
                                        {(formik.touched.idEvento || formik.errors.idEvento) && (
                                            <Typography sx={{ color: 'rgb(211,31,43)', fontSize: 13, marginTop: 1, ml: 4 }}>
                                                {formik.errors.idEvento}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Card sx={{ width: "100%", height: "100%", marginTop: 2, borderRadius: "9px", boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)", display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Grid container spacing={2} height={"100%"}>
                                    <Grid item xs={12}>
                                        <Box padding={2} sx={{ flex: 1 }}>
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                                                {selectedEvent.map((ubication) => (
                                                    <Chip
                                                        color="primary"
                                                        key={ubication.id}
                                                        label={ubication.Actividad}
                                                        onDelete={() => {
                                                            setSelectedEvent([])
                                                            formik.setFieldValue('idEvento', 0)
                                                        }}
                                                        sx={{ fontSize: 14 }}
                                                        disabled={isDisabled}
                                                    />
                                                ))}
                                            </Box>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} lg={5} sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <TextField
                                                        label="Seleccionar imagen"
                                                        size="small"
                                                        name="files"
                                                        fullWidth
                                                        value={file ? file.name : ''}  // Mostramos el nombre del archivo seleccionado
                                                        onClick={() => document.getElementById('file-image').click()}
                                                        onChange={(event) => {
                                                            document.getElementById('file-image').click();
                                                            formik.handleChange(event); // Asegúrate de que se maneje correctamente el cambio en formik
                                                        }}
                                                        error={verify('files')?.error}
                                                        helperText={verify('files')?.helperText}
                                                        InputLabelProps={{ shrink: true }}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <label htmlFor="file-image">
                                                                    <IconButton component="span" aria-label="upload img" color="primary">
                                                                        <Icon>upload_file</Icon>
                                                                    </IconButton>
                                                                </label>
                                                            ),
                                                        }}
                                                        sx={{
                                                            minHeight: '50px', // Para evitar que el campo cambie de tamaño cuando aparece el helperText
                                                        }}
                                                    />
                                                    <input
                                                        id="file-image"
                                                        name="img"
                                                        type="file"
                                                        accept=".png,.jpg,.jpeg"
                                                        style={{ display: 'none' }}
                                                        onChange={handleFileChange}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} lg={3}>
                                                    <Button fullWidth variant="outlined" color="primary" onClick={handleAddImage} disabled={!file}>
                                                        Agregar
                                                    </Button>
                                                </Grid>
                                            </Grid>

                                            <Grid container spacing={2} marginTop={2} position="relative" sx={{ overflowY: 'auto', maxHeight: '450px' }}>
                                                {formik.values.files?.map((item, index) => {
                                                    return (
                                                        <Grid item xs={6} key={index}>
                                                            <Box sx={{ position: "relative", width: "100%", height: "200px" }}>
                                                                <CardMedia
                                                                    component="img"
                                                                    image={item.file}
                                                                    sx={{ width: "100%", height: "100%", borderRadius: 2 }}
                                                                />
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{ position: "absolute", top: 5, right: 5, bgcolor: "rgba(0,0,0,0.5)" }}
                                                                    onClick={() => handleRemoveImage(index)}
                                                                >
                                                                    <Close sx={{ color: "#fff", fontSize: 14 }} />
                                                                </IconButton>
                                                            </Box>
                                                        </Grid>
                                                    )
                                                }
                                                )}
                                            </Grid>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 1 }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={resetParams}
                            sx={{ width: { xs: "35%", md: "10%" }, backgroundColor: red[600], color: '#fff', ':hover': { backgroundColor: red[700], color: '#fff' } }}
                        >
                            Limpiar
                        </Button>
                        <Button
                            variant="contained" color="primaryDark" size="small" onClick={formik.submitForm}
                            sx={{ width: { xs: "35%", md: "10%" } }}
                        >
                            Guardar
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal >
    );
};

export default FormGaleria;
